import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import { EMAIL_REGEX } from "../../../utils/email";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 520px;
  max-width: 95vw;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;

const Title = styled.h3`
  margin: 0 0 20px;
  font-size: 16px;
  color: #212529;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #495057;
`;

const Input = styled.input`
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
  &:focus { outline: none; border-color: #0d6efd; }
  &:disabled { background: #f1f3f5; color: #868e96; cursor: not-allowed; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const Btn = styled.button`
  padding: 7px 18px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: ${(p) => (p.$primary ? "#0d6efd" : "#6c757d")};
  color: #fff;
  &:disabled { opacity: 0.5; }
`;

const ErrorText = styled.span`
  font-size: 11px;
  color: #dc3545;
`;

const LimitText = styled.span`
  font-size: 11px;
  color: #868e96;
`;

// ── Previously invited sites ─────────────────────────────────────────────────

const Directory = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const DirectoryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DirectoryTitle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
`;

const SearchInput = styled(Input)`
  width: 100%;
  margin-top: 8px;
  background: #fff;
`;

const ResultList = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 168px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fff;
`;

const ResultItem = styled.li`
  padding: 7px 10px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover { background: #e7f1ff; }
`;

const ResultMain = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #212529;
`;

const ResultSub = styled.div`
  font-size: 11px;
  color: #868e96;
  margin-top: 2px;
`;

const DirectoryNote = styled.div`
  font-size: 11px;
  color: ${(p) => (p.$error ? "#dc3545" : "#868e96")};
  margin-top: 8px;
`;

const Notice = styled.div`
  border: 1px solid ${(p) => (p.$warn ? "#ffe69c" : "#e9ecef")};
  background: ${(p) => (p.$warn ? "#fff9e6" : "#f8f9fa")};
  border-radius: 6px;
  padding: 9px 12px;
  margin-bottom: 16px;
  font-size: 11px;
  line-height: 1.6;
  color: ${(p) => (p.$warn ? "#664d03" : "#6c757d")};
`;

// 자동 알림의 사이트별 예외를 담는 상자. Notice와 달리 테두리를 세워 두는 이유는 여기에
// 입력이 들어 있기 때문이다 — 읽고 지나가는 안내가 아니라 고치는 자리다.
const ReminderBox = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 11px 13px;
  margin-bottom: 16px;
  background: #f8f9fa;
`;

const ReminderToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #212529;
  cursor: pointer;
  input { cursor: pointer; margin: 0; }
`;

const ReminderCadence = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 9px;
  font-size: 12px;
  color: ${(p) => (p.$disabled ? "#adb5bd" : "#495057")};
  span { color: #adb5bd; font-size: 11px; }
  input {
    width: 66px;
    padding: 4px 6px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
    text-align: right;
    &:disabled { background: #f1f3f5; color: #adb5bd; }
  }
`;

const ReminderNote = styled.div`
  margin-top: 9px;
  font-size: 11px;
  line-height: 1.6;
  color: #6c757d;
  b { color: #495057; }
`;

// YYYY-MM-DD in Korean time (KST, UTC+9).
function kstDateStr(date) {
  return new Date(date.getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

// Default expiration: one month from today (KST).
function defaultExpiry() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return kstDateStr(d);
}

const INITIAL = {
  siteCode: "", siteName: "", siteCountry: "",
  piName: "", piEmail: "",
  crcName: "", crcEmail: "",
  expiryDate: defaultExpiry(),
  // 자동 알림의 사이트별 예외. 빈 문자열인 주기는 "스터디 기본값을 따른다"는 뜻이고,
  // 저장할 때 null로 나간다 — 0이나 -1 같은 센티널을 두지 않는 이유는 API 쪽 주석 참고.
  reminderOptOut: false,
  reminderIntervalDays: "",
};

// Column widths on SFQ_SiteInvitation. The API rejects anything longer with a 400, so
// the form stops the text at the input instead of at the save — and says it is doing so,
// since a paste that silently loses its tail is worse than one that never lands.
const FIELD_LIMITS = {
  siteCode:    50,
  siteName:    200,
  siteCountry: 100,
  piName:      100,
  piEmail:     200,
  crcName:     100,
  crcEmail:    200,
};

function FormField({ label, field, required, form, onChange, error, disabled, title }) {
  const max    = FIELD_LIMITS[field];
  const value  = form[field];
  const atCap  = max && value.length >= max;

  return (
    <Field>
      <Label>{label}{required && " *"}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={label}
        maxLength={max}
        disabled={disabled}
        title={title}
      />
      {error && <ErrorText>{error}</ErrorText>}
      {!error && atCap && <LimitText>최대 {max}자까지 입력할 수 있습니다.</LimitText>}
    </Field>
  );
}

// Sites already invited in another study (or an earlier round of this one). Refetched
// on every keystroke of the search box, debounced, with stale responses dropped so a
// slow request can never overwrite the results of a later one.
function useSiteDirectory(enabled, query) {
  const [sites, setSites]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      sfqApi.siteDirectory(query.trim())
        .then((res) => {
          if (cancelled) return;
          setSites(res.data?.data || []);
          setError(null);
        })
        .catch(() => !cancelled && setError("이전 발송 사이트를 불러오지 못했습니다."))
        .finally(() => !cancelled && setLoading(false));
    }, query.trim() ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, query]);

  return { sites, loading, error };
}

// The invitation's own contact fields — everything a past site can prefill. The expiry
// is deliberately left alone: it belongs to this invitation, not to the site.
const SITE_FIELDS = [
  "siteCode", "siteName", "siteCountry",
  "piName", "piEmail", "crcName", "crcEmail",
];

const siteKey = (s) =>
  [s.siteCode, s.siteName, s.piEmail].map((v) => (v || "").trim().toLowerCase()).join("|");

const LOCK_HINT =
  "발송된 메일에 이미 적힌 항목이라 수정할 수 없습니다. 담당자 정보와 만료일은 수정할 수 있습니다.";

// Prefills the form from a row the grid already has.
function formFrom(inv) {
  return {
    ...SITE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: inv[f] || "" }), {}),
    expiryDate: inv.siteTokenExpiry ? kstDateStr(new Date(inv.siteTokenExpiry)) : defaultExpiry(),
    reminderOptOut: !!inv.reminderOptOut,
    reminderIntervalDays: inv.reminderIntervalDays ? String(inv.reminderIntervalDays) : "",
  };
}

// `invitation` switches the modal into editing that row; without it this adds a new one.
export default function AddSiteModal({ invitation, onSubmit, onClose }) {
  const editing        = Boolean(invitation);
  const identityLocked = editing && invitation.status !== "Pending";

  const [initial]           = useState(() => (editing ? formFrom(invitation) : INITIAL));
  const [form, setForm]     = useState(initial);
  const [saving, setSaving] = useState(false);

  const [dirOpen, setDirOpen]     = useState(false);
  const [dirQuery, setDirQuery]   = useState("");
  const [pickedKey, setPickedKey] = useState(null);
  const { sites, loading: dirLoading, error: dirError } = useSiteDirectory(dirOpen, dirQuery);

  function set(field, value) {
    setForm((p) => ({ ...p, [field]: value }));
  }

  // Prefill from a past invitation. Blank fields on the source site clear the
  // corresponding input rather than leaving the previous pick's value behind.
  function applySite(site) {
    setForm((p) => ({
      ...p,
      ...SITE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: site[f] || "" }), {}),
    }));
    setPickedKey(siteKey(site));
  }

  const emailErrors = {
    piEmail:
      form.piEmail && !EMAIL_REGEX.test(form.piEmail)
        ? "올바른 이메일 형식이 아닙니다."
        : "",
    crcEmail:
      form.crcEmail && !EMAIL_REGEX.test(form.crcEmail)
        ? "올바른 이메일 형식이 아닙니다."
        : "",
  };
  const hasEmailError = Boolean(emailErrors.piEmail || emailErrors.crcEmail);

  const dirty = Object.keys(initial).some((f) => form[f] !== initial[f]);

  // Changing the address does not reach whoever received the original mail: their link
  // and password stay valid until the Resend that delivers to the new one. Saying so
  // here is the difference between "fixed the typo" and "fixed the typo and it arrived".
  const emailMoved = identityLocked && form.piEmail !== initial.piEmail;

  const incomplete = !form.siteName || !form.piName || !form.piEmail || !form.expiryDate;

  // 빈 값은 "스터디 기본값"이라 오류가 아니다. 범위는 API·DB의 CHECK와 같은 1~90일.
  const intervalError = form.reminderIntervalDays !== ""
    && !(parseInt(form.reminderIntervalDays, 10) >= 1 && parseInt(form.reminderIntervalDays, 10) <= 90)
    ? "1일에서 90일 사이로 입력해 주세요."
    : "";

  async function handleSubmit() {
    if (incomplete || hasEmailError) return;
    if (editing && !dirty) return;
    if (intervalError) return;
    setSaving(true);
    try {
      // Expire at 23:59:59.999 Korean time (KST, UTC+9) of the selected date.
      const expiry = new Date(`${form.expiryDate}T23:59:59.999+09:00`).toISOString();
      const { expiryDate, reminderIntervalDays, ...rest } = form;
      await onSubmit({
        ...rest,
        expiry,
        // 비워 둔 주기는 null로 — API가 그걸 "스터디 기본값을 따른다"로 읽는다.
        reminderIntervalDays: reminderIntervalDays === "" ? null : parseInt(reminderIntervalDays, 10),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Backdrop>
      <Modal>
        <Title>{editing ? "Edit Site Invitation" : "Add Site Invitation"}</Title>

        {identityLocked && (
          <Notice>
            이미 발송된 사이트입니다. Site Code / Name / Country는 발송된 메일 내용과
            어긋나지 않도록 잠겨 있으며, 담당자 정보와 만료일만 수정할 수 있습니다.
          </Notice>
        )}

        {!editing && (
        <Directory>
          <DirectoryHead>
            <DirectoryTitle type="button" onClick={() => setDirOpen((o) => !o)}>
              {dirOpen ? "▾" : "▸"} 이전에 발송한 사이트 불러오기
            </DirectoryTitle>
            {dirOpen && dirLoading && <ResultSub>불러오는 중...</ResultSub>}
          </DirectoryHead>

          {dirOpen && (
            <>
              <SearchInput
                value={dirQuery}
                onChange={(e) => setDirQuery(e.target.value)}
                placeholder="Site Code / Site Name / Country / PI / CRC 검색"
              />
              {dirError && <DirectoryNote $error>{dirError}</DirectoryNote>}
              {!dirError && !dirLoading && sites.length === 0 && (
                <DirectoryNote>
                  {dirQuery.trim()
                    ? "검색 결과가 없습니다."
                    : "이전에 발송한 사이트가 없습니다."}
                </DirectoryNote>
              )}
              {sites.length > 0 && (
                <ResultList>
                  {sites.map((s) => (
                    <ResultItem key={`${s.sfqId}-${siteKey(s)}`} onClick={() => applySite(s)}>
                      <ResultMain>
                        {s.siteName || "(이름 없음)"}
                        {s.siteCode ? ` · ${s.siteCode}` : ""}
                        {s.siteCountry ? ` · ${s.siteCountry}` : ""}
                      </ResultMain>
                      <ResultSub>
                        PI {s.piName || "-"}{s.piEmail ? ` <${s.piEmail}>` : ""}
                        {" · "}{s.studyCode || `SFQ #${s.sfqId}`}
                        {s.lastSentAt ? ` · 최근 발송 ${kstDateStr(new Date(s.lastSentAt))}` : ""}
                        {s.sentCount > 1 ? ` (${s.sentCount}회)` : ""}
                      </ResultSub>
                    </ResultItem>
                  ))}
                </ResultList>
              )}
              {pickedKey && (
                <DirectoryNote>
                  선택한 사이트 정보를 아래 입력란에 채웠습니다. 필요하면 수정하세요.
                </DirectoryNote>
              )}
            </>
          )}
        </Directory>
        )}

        <Row>
          <FormField label="Site Code"    field="siteCode"    form={form} onChange={set}
            disabled={identityLocked} title={identityLocked ? LOCK_HINT : undefined} />
          <FormField label="Site Name"    field="siteName"    form={form} onChange={set} required
            disabled={identityLocked} title={identityLocked ? LOCK_HINT : undefined} />
        </Row>
        <Row>
          <FormField label="Country"      field="siteCountry" form={form} onChange={set}
            disabled={identityLocked} title={identityLocked ? LOCK_HINT : undefined} />
        </Row>
        <Row>
          <FormField label="PI Name"      field="piName"      form={form} onChange={set} required />
          <FormField label="PI Email"     field="piEmail"     form={form} onChange={set} required error={emailErrors.piEmail} />
        </Row>
        <Row>
          <FormField label="CRC Name"     field="crcName"     form={form} onChange={set} />
          <FormField label="CRC Email"    field="crcEmail"    form={form} onChange={set} error={emailErrors.crcEmail} />
        </Row>
        {form.crcEmail && (
          <Notice>
            CRC는 초대 메일의 <b>참조(Cc)</b>로만 발송되며, 받는 사람은 PI입니다.
            설문 접속 비밀번호는 PI에게만 발송되므로 <b>설문 작성·제출은 PI 본인만</b> 할 수 있습니다.
            발송 화면에서 참조 여부를 끌 수도 있습니다.
          </Notice>
        )}
        <Row>
          <Field>
            <Label>만료일 (Expiration Date) *</Label>
            <Input
              type="date"
              value={form.expiryDate}
              min={kstDateStr(new Date())}
              onChange={(e) => set("expiryDate", e.target.value)}
            />
            {identityLocked && (
              <ErrorText as="span" style={{ color: "#868e96" }}>
                만료일을 미루면 사이트가 받은 링크와 비밀번호를 그대로 쓸 수 있습니다.
              </ErrorText>
            )}
          </Field>
        </Row>

        {/* 자동 알림의 사이트별 예외. 등록할 때부터 정할 수 있다 — "이 사이트에는 재촉하지
            말라"가 이미 정해져 있는 경우가 있고, 그렇다면 초대를 보낸 뒤 Edit 으로 다시
            들어가 끄게 만들 이유가 없다. 그 사이에 한 번 나가버릴 수도 있다. */}
        <ReminderBox>
          <ReminderToggle>
            <input
              type="checkbox"
              checked={!!form.reminderOptOut}
              onChange={(e) => set("reminderOptOut", e.target.checked)}
            />
            이 사이트에는 자동 알림 메일을 보내지 않는다
          </ReminderToggle>

          <ReminderCadence $disabled={!!form.reminderOptOut}>
            알림 주기:
            <input
              type="number" min={1} max={90}
              value={form.reminderIntervalDays}
              disabled={!!form.reminderOptOut}
              placeholder="기본값"
              onChange={(e) => set("reminderIntervalDays", e.target.value)}
            />
            일 <span>(비워 두면 스터디 기본 주기를 따릅니다)</span>
          </ReminderCadence>
          {intervalError && <ErrorText>{intervalError}</ErrorText>}

          <ReminderNote>
            자동 알림은 스터디의 <b>⏰ 알림 Email Template</b>에서 켜야 나갑니다. 여기서 정하는
            것은 이 사이트만의 예외입니다. 알림 메일은 새 비밀번호를 발급하지 않습니다.
            {!editing && <> 알림은 <b>초대 메일을 보낸 뒤</b>부터 이 주기로 나갑니다.</>}
          </ReminderNote>
        </ReminderBox>

        {emailMoved && (
          <Notice $warn>
            PI 이메일을 변경했습니다. 기존 주소로 보낸 메일의 링크와 비밀번호는 그대로
            살아 있으며, 새 주소로는 저장 후 <b>Resend</b>를 해야 전달됩니다. Resend 시
            비밀번호가 새로 발급되어 이전 메일은 사용할 수 없게 됩니다.
          </Notice>
        )}

        <Footer>
          <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn
            $primary
            onClick={handleSubmit}
            disabled={incomplete || hasEmailError || saving || (editing && !dirty)}
          >
            {saving
              ? (editing ? "Saving..." : "Adding...")
              : (editing ? "Save" : "Add Site")}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
